import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Container, Button, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Alert, TextField } from '@mui/material';

import { ICustomerExportRequest, ICustomerLight } from "../../../types/customer";
import { ISelectOption } from "../../../types/list";

import { AppConfig } from "../../../config/appConfig";
import { useUserContext } from "../../../contexts/userContext";

import { CustomerService } from "../../../services/customerService";
import { UtilityService } from "../../../services/utilityService";
import { ErrorProvider } from "../../../classes/errorProvider";

import CustomerTable from "../../../components/tables/customerTable";
import Navigation from "../../../components/header";
import PermissionGate from "../../../components/permissionGate";
import Spinner from "../../../components/spinner";
import Json from "../../json";
import WorkingModal, { IWorkingModalProps } from "../../workingModal";

import "../../../styles/entity/customerList.scss";

const CustomerList = () => {
	const userContext = useUserContext();
	const [gridView, setGridView] = useState<ICustomerLight[]>();

	//Filtering
	const [brandOptions, setBrandOptions] = useState<ISelectOption[]>([]);
	const [stateOptions, setStateOptions] = useState<string[]>([]);
	const [cityOptions, setCityOptions] = useState<string[]>([]);
	const [statusOptions, setStatusOptions] = useState<string[]>([]);
	const [nameFilter, setNameFilter] = useState("");
	const [brandFilter, setBrandFilter] = useState("All");
	const [cityFilter, setCityFilter] = useState("All");
	const [stateFilter, setStateFilter] = useState("All");
	const [statusFilter, setStatusFilter] = useState("All");
	const [filteredResultCount, setFilteredResultCount] = useState(0);

	const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);
	const [exportSortField, setExportSortField] = useState("");
	const [exportSortAscending, setExportSortAscending] = useState(true);

	const loadCustomerData = async () => {
		setIsLoadingCustomers(true);
		setErrorMessage(undefined);

		CustomerService.getCustomers()
			.then((response) => {
				console.log('CustomerService.getCustomers: SUCCESS', response);

				let brands: ISelectOption[] = [];
				let cities: string[] = [];
				let states: string[] = [];
				let statuses: string[] = [];

				let customers = CustomerService.mapCustomerLightList(response.data);

				customers.map((item) => {
					if (item.brandId && !brands.some(b => b.value === item.brandId)) {
						brands.push({ name: item.brand, value: item.brandId });
					}

					if (item.city && !cities.includes(item.city)) {
						cities.push(item.city);
					}

					if (item.state && !states.includes(item.state)) {
						states.push(item.state);
					}

					if (item.status && !statuses.includes(item.status)) {
						statuses.push(item.status);
					}
				});

				setGridView(customers);
				setBrandOptions(brands.sort());
				setCityOptions(cities.sort());
				setStateOptions(states.sort());
				setStatusOptions(statuses.sort());
			})
			.catch((error) => {
				console.log('CustomerService.getCustomers: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
			})
			.finally(() => {
				setIsLoadingCustomers(false);
			});
	}

	useEffect(() => {
		loadCustomerData();
	}, [])

	const handleSelectBrand = (event: SelectChangeEvent) => {
		setBrandFilter(event.target.value);
	};

	const handleSelectState = (event: SelectChangeEvent) => {
		setStateFilter(event.target.value);
	};

	const handleSelectCity = (event: SelectChangeEvent) => {
		setCityFilter(event.target.value);
	};

	const handleSelectStatus = (event: SelectChangeEvent) => {
		setStatusFilter(event.target.value);
	};

	const handleFilteredResultsChanged = (results: ICustomerLight[]) => {
		setFilteredResultCount(results?.length ?? 0);
	};

	const handleSortOptionsChanged = (sortField: string, sortAscending: boolean) => {
		setExportSortField(sortField);
		setExportSortAscending(sortAscending);
	};

	const handleExportCustomers = () => {
		setWorkingModalParams({ params: { content: "Exporting customers..." } });

		var request: ICustomerExportRequest = {
			name: nameFilter?.trim() ? nameFilter?.trim() : undefined,
			brand: +brandFilter > 0 ? +brandFilter : undefined,
			state: stateFilter == "All" ? undefined : stateFilter,
			city: cityFilter == "All" ? undefined : cityFilter,
			status: statusFilter == "All" ? undefined : statusFilter.toLowerCase() == "active",
			sortField: exportSortField,
			sortAscending: exportSortAscending
		};

		CustomerService.exportCustomerListToExcel(request)
			.then((response) => {
				console.log('CustomerService.exportCustomerListToExcel: SUCCESS', response);
				UtilityService.downloadGeneratedDocument(response.data);
			})
			.catch((error) => {
				console.log('CustomerService.exportCustomerListToExcel: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setWorkingModalParams(null);
			});
	};

	return (
		<div className="hwi-customer-list">
			<Navigation></Navigation>
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={userContext.permissions?.canManageCustomers}>
					<section className="hwi-entity-search-bar">
						<div className="search-header">
							<div className="title">Search Customers</div>
							<div className="links">
								{userContext.permissions?.canExportCustomers && <Button variant="contained" color="primary" disabled={isLoadingCustomers || !filteredResultCount} onClick={handleExportCustomers}>Export Customers</Button>}
								{userContext.permissions?.canChangeClosedThroughDate && <NavLink to="/customer/closing"><Button variant="outlined" color="primary">Close Customers</Button></NavLink>}
								<NavLink to="/customer/add"><Button variant="outlined">New Customer</Button></NavLink>
							</div>
						</div>
						<section className="search-options">
							<FormControl size="small">
								<TextField 
									label="Name" 
									name="name" 
									value={nameFilter} 
									size="small" 
									autoComplete="off"
									onChange={(event) => { setNameFilter(event.target.value) }}
								></TextField>
							</FormControl>
							<FormControl size="small">
								<InputLabel>Brand</InputLabel>
								<Select
									value={brandFilter}
									defaultValue="All"
									label="Brand"
									onChange={handleSelectBrand}
								>
									<MenuItem value="All" key="-1">All</MenuItem>
									{brandOptions.map((item, index) => (
										<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small">
								<InputLabel>State</InputLabel>
								<Select
									value={stateFilter}
									label="State"
									onChange={handleSelectState}
								>
									<MenuItem value="All" key="-1">All</MenuItem>
									{stateOptions.map((item, index) => (
										<MenuItem value={item} key={index}>{item}</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small">
								<InputLabel>City</InputLabel>
								<Select
									value={cityFilter}
									label="City"
									onChange={handleSelectCity}
								>
									<MenuItem value="All" key="-1">All</MenuItem>
									{cityOptions.map((item, index) => (
										<MenuItem value={item} key={index}>{item}</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small">
								<InputLabel>Status</InputLabel>
								<Select
									value={statusFilter}
									label="Status"
									onChange={handleSelectStatus}
								>
									<MenuItem value="All" key="-1">All</MenuItem>
									{statusOptions.map((item, index) => (
										<MenuItem value={item} key={index}>{item}</MenuItem>
									))}
								</Select>
							</FormControl>
						</section>
					</section>
					<section className="results">
						<Spinner dependency={isLoadingCustomers} text="Loading...">
							<section className="results">
								{errorMessage &&
									<Alert className="alert-error"
										severity="error">
										{errorMessage}
									</Alert>
								}
								{!errorMessage &&
									<CustomerTable 
										data={gridView} 
										name={nameFilter} 
										brand={brandFilter} 
										state={stateFilter} 
										city={cityFilter} 
										status={statusFilter} 
										onVisibleRowsChanged={handleFilteredResultsChanged}
										onSortOptionschanged={handleSortOptionsChanged}
									></CustomerTable>
								}
							</section>
						</Spinner>
					</section>
				</PermissionGate>
				<WorkingModal params={workingModalParams?.params}></WorkingModal>
			</Container>
			{/* <Json data={brandFilter}></Json> */}
		</div>
	);
};

export default CustomerList;